import { PencilIcon } from '@heroicons/react/24/solid';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router';
import { type GetCommentParams, getPartnerProposalComments, postComment } from '@/api/comments';
import { getTdmsByProjectPhase } from '@/api/tdm';
import Comments from '@/components/Comments';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import Badge from '@/components/UI/Badge';
import HistoryTimeline from '@/components/UI/HistoryTimeline';
import { TdmReactQueryKey } from '@/constants/ReactQueryKeys';
import { useInvoice } from '@/hooks/ReactQueryHooks/useProjectDrawQuery';
import { useProject } from '@/hooks/ReactQueryHooks/useProjectQuery';
import { useUsers } from '@/hooks/ReactQueryHooks/useUsersQuery';
import type { User } from '@/types/User';
import { calculateDaysLeft, convertEnumToText, formatDate3, formatNumber, getInitials } from '@/utils';
import Approvals from '../../components/Approvals/index.tsx';
import { EditInvoice } from '../EditInvoice';

const InvoiceDetails = () => {
  const { projectId, drawId, invoiceId } = useParams<string>();
  const fetchProject = useProject({ projectId: parseInt(projectId!, 10) });
  const project = fetchProject?.data?.data;
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const handleModel = () => {
    setIsEditing(!isEditing);
  };

  const fetchInvoice = useInvoice({
    projectId: Number(projectId),
    drawId: Number(drawId),
    invoiceId: Number(invoiceId),
  });
  const invoice = fetchInvoice?.data?.data;

  const fetchUsers = useUsers({ params: { active: 'true' } });

  const users = fetchUsers.data?.data as User[];

  const createdBy = users?.find((member) => member?.id === invoice?.createdById);

  //Tanstack: version 5 query implementation
  const fetchDeliverables = useQuery({
    queryKey: [TdmReactQueryKey.GET_PROJECT_DELIVERABLES, projectId, invoice?.phaseSlug],
    queryFn: () =>
      getTdmsByProjectPhase({
        type: 'deliverables',
        phase: invoice?.phaseSlug,
        page: 1,
        perPage: 10,
        projectId: Number(projectId),
      }),
  });
  const deliverables = fetchDeliverables?.data?.data;

  //Tanstack: version 5 query implementation
  const fetchMilestones = useQuery({
    queryKey: [TdmReactQueryKey.GET_PROJECT_MILESTONES, projectId, invoice?.phaseSlug],
    queryFn: () =>
      getTdmsByProjectPhase({
        type: 'milestones',
        phase: invoice?.phaseSlug,
        page: 1,
        perPage: 10,
        projectId: Number(projectId),
      }),
  });
  const milestones = fetchMilestones?.data?.data;

  const postComments = useCallback(
    async (values: { comment: string }) => {
      const response = await postComment({
        data: {
          resource_id: invoiceId!,
          resource_type: 'ProjectDrawInvoice',
          ...values,
        },
      });
      return response;
    },
    [invoiceId]
  );

  const getComments = useCallback(
    (_params: GetCommentParams) => {
      return getPartnerProposalComments({
        params: {
          resource_id: invoiceId!,
          resource_type: 'ProjectDrawInvoice',
        },
      });
    },
    [invoiceId]
  );

  return (
    <>
      {isEditing && (
        <EditInvoice
          handleModal={handleModel}
          invoice={invoice}
          projectName={project?.name || ''}
          deliverables={deliverables}
          milestones={milestones}
        />
      )}
      {invoice && (
        <div className='min-h-full max-w-screen-2xl mx-auto'>
          <main>
            <div className='sm:px-6 mt-8 w-full mx-auto grid grid-cols-12 gap-6'>
              <div className='space-y-6 col-span-12 xl:col-span-8'>
                <section>
                  <div className='bg-white shadow sm:rounded-lg px-4 py-5'>
                    <div className='py-2 px-4 flex justify-between items-center border-b-2'>
                      <h2 className='capitalize'>Invoice #: {invoice?.invoiceNumber}</h2>
                      <div className='flex justify-between items-center gap-3'>
                        <Badge status={invoice?.status} text={convertEnumToText(invoice?.status)} />
                        {invoice?.status !== 'APPROVED' && (
                          <PencilIcon className='h-5 w-5 text-neutralColor-normal cursor-pointer' onClick={handleModel} />
                        )}
                      </div>
                    </div>
                    <div className='py-4'>
                      {project && createdBy && (
                        <div className='flex flex-col justify-between px-2'>
                          <h4 className='font-semibold'>Submitted By</h4>
                          <div className='flex pt-2'>
                            <Link to={`/app/administration/users/${createdBy?.id}`} className='flex items-center gap-2'>
                              <ImageCircularAvatar
                                customClass='h-16 w-16'
                                imageUrl={createdBy?.profileImageUrl as string}
                                fallbackText={getInitials(createdBy?.name)}
                              />
                              <div>
                                <p className='text-base font-semibold'>{createdBy?.name}</p>
                                <p className='text-neutralColor-normal text-sm'>{createdBy?.title}</p>
                              </div>
                            </Link>
                          </div>
                        </div>
                      )}
                      <div className='grid grid-cols-2 py-6 border-b-2 px-2'>
                        <div className='col-span-1'>
                          <div className='flex flex-col items-start gap-3'>
                            <div>
                              <span className='text-neutralColor-normal'>Submitter Email:</span>
                              <span className=''> {createdBy?.email}</span>
                            </div>
                          </div>
                        </div>
                        <div className='col-span-1 justify-self-center'>
                          <div className='flex flex-col items-start gap-3'>
                            <div>
                              <span className='text-neutralColor-normal'>Submitted:</span>
                              <span> {formatDate3(invoice?.createdAt)}</span>
                            </div>
                            <div>
                              <span className='text-neutralColor-normal'>Due Date:</span>
                              <span> {formatDate3(invoice?.dueDate)}</span>
                            </div>
                            <div>
                              <span className='text-neutralColor-normal'>Days Left: </span>
                              <span>{calculateDaysLeft(invoice?.dueDate)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='border-b-2 px-2'>
                        <h3 className='font-semibold py-4 flex justify-end'>Invoice Amount : $ {formatNumber(invoice?.amount)}</h3>
                      </div>
                      <div className='flex flex-col space-y-2 py-4 px-2'>
                        <div>
                          <h3 className='font-semibold'>Notes</h3>
                          <p className='text-neutralColor-normal'>{invoice?.notes ?? 'None'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
                <Comments postComments={postComments} getComments={getComments} queryKey='invoice-comments' />
              </div>
              <div className='space-y-6 hidden xl:block xl:col-span-4'>
                <Approvals approvals={invoice?.approval} members={users} type='invoice' />
                <HistoryTimeline activity={invoice} resourceType='ProjectDrawInvoice' />
              </div>
            </div>
          </main>
        </div>
      )}
    </>
  );
};

export default InvoiceDetails;
